import cloneDeep from 'lodash/cloneDeep';
import {
  doiDefinition,
  issnDefinition,
  isbnDefinition,
  landingPageUrlDefinition,
  similarityCheckUrlDefinition,
  publisherDefinition,
  referencesDefinition,
  languageCodesDefinition,
  contributorDefinition,
  contributorsDefinition,
  optionalContributorsDefinition,
  abstractsDefinition,
  publicationDatesDefinition,
} from '../common/common-schemas';

/** Customized contributor definition for books that replaces the editor role with explicit first and additional editor roles. */
const bookContributorDefinition = cloneDeep(contributorDefinition);
bookContributorDefinition.properties.role.enum = [
  'first-author',
  'additional-author',
  'first-editor',
  'additional-editor',
  'chair',
  'reviewer',
  'review-assistant',
  'stats-reviewer',
  'reviewer-external',
  'reader',
  'translator',
];

const bookPublicationDatesDefinition = {
  ...publicationDatesDefinition,
  i18n: 'book_publicationDates',
  properties: {
    online: {
      ...publicationDatesDefinition.properties.online,
      i18n: 'book_publicationDates_online',
    },
    print: {
      ...publicationDatesDefinition.properties.print,
      i18n: 'book_publicationDates_print',
    },
  },
};

const bookIsbnDefinition = {
  ...isbnDefinition,
  i18n: 'book_isbn',
  properties: {
    electronicIsbn: {
      ...isbnDefinition.properties.electronicIsbn,
      i18n: 'book_electronicIsbn',
    },
    printIsbn: {
      ...isbnDefinition.properties.printIsbn,
      i18n: 'book_printIsbn',
    },
  },
};

const commonDefinitions = {
  abstracts: abstractsDefinition,
  contributor: bookContributorDefinition,
  contributors: contributorsDefinition,
  doi: doiDefinition,
  isbn: isbnDefinition,
  'crossref_language_codes': languageCodesDefinition,
  issn: issnDefinition,
  landingPageUrl: landingPageUrlDefinition,
  optionalContributors: optionalContributorsDefinition,
  publicationDates: publicationDatesDefinition,
  publisher: publisherDefinition,
  references: referencesDefinition,
  similarityCheckUrl: similarityCheckUrlDefinition,
};

export const bookSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Book Metadata',
  type: 'object',
  required: [
    'bookType',
    'title',
    'doi',
    'landingPageUrl',
    'contributors',
    'publicationDates',
    'publisher',
  ],
  properties: {
    bookType: {
      type: 'string',
      enum: ['monograph', 'edited_book', 'reference', 'other'],
      i18n: 'book_bookType',
    },
    title: {
      type: 'string',
      i18n: 'book_title',
    },
    subtitle: {
      type: 'string',
      i18n: 'book_subtitle',
    },
    language: {
      ...languageCodesDefinition,
      i18n: 'book_language',
    },
    editionNumber: {
      type: 'string',
      i18n: 'book_editionNumber',
    },
    doi: {
      ...doiDefinition,
      i18n: 'book_doi',
    },
    landingPageUrl: {
      ...landingPageUrlDefinition,
      i18n: 'book_landingPageUrl',
    },
    contributors: {
      $ref: '#/definitions/contributors',
    },
    abstracts: {
      $ref: '#/definitions/abstracts',
    },
    publicationDates: bookPublicationDatesDefinition,
    publisher: {
      ...publisherDefinition,
      i18n: 'book_publisher',
    },
    isbn: bookIsbnDefinition,
    similarityCheckUrl: {
      ...similarityCheckUrlDefinition,
      i18n: 'book_similarityCheckUrl',
    },
    references: {
      ...referencesDefinition,
      i18n: 'book_references',
    },
  },
  definitions: {
    ...commonDefinitions,
  },
};

export const bookChaptersSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Book Chapters Metadata',
  type: 'object',
  properties: {
    bookChapters: {
      type: 'array',
      items: {
        $ref: '#/definitions/bookChapter',
      },
    },
  },
  definitions: {
    ...commonDefinitions,
    bookChapter: {
      type: 'object',
      required: ['chapterTitle', 'doi', 'landingPageUrl'],
      properties: {
        chapterTitle: {
          type: 'string',
          i18n: 'chapter_title',
        },
        doi: {
          ...doiDefinition,
          i18n: 'chapter_doi',
        },
        landingPageUrl: {
          ...landingPageUrlDefinition,
          i18n: 'chapter_landingPageUrl',
        },
        contributors: {
          $ref: '#/definitions/optionalContributors',
        },
        abstracts: {
          $ref: '#/definitions/abstracts',
        },
        pageOrChapterNumber: {
          type: 'object',
          i18n: 'chapter_pageOrChapterNumber',
          properties: {
            firstPageNumber: {
              type: 'string',
              pattern: '^[0-9a-zA-Z]+$',
              i18n: 'chapter_pageOrChapterNumber_firstPageNumber',
            },
            chapterNumber: {
              type: 'string',
              i18n: 'chapter_pageOrChapterNumber_chapterNumber',
            },
          },
        },
        references: {
          ...referencesDefinition,
          i18n: 'chapter_references',
        },
      },
    },
  },
};

export const bookSeriesSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Book Series Metadata',
  type: 'object',
  required: ['seriesTitle', 'issn'],
  properties: {
    seriesTitle: {
      type: 'string',
      i18n: 'series_title',
    },
    language: {
      ...languageCodesDefinition,
      i18n: 'series_language',
    },
    issn: {
      ...issnDefinition,
      i18n: 'series_issn',
    },
    contributors: {
      $ref: '#/definitions/optionalContributors',
    },
  },
  definitions: {
    ...commonDefinitions,
  },
};

export const bookRecordSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Book',
  type: 'object',
  required: ['bookMetadata'],
  properties: {
    bookMetadata: bookSchema,
    series: bookSeriesSchema,
    chapters: {
      type: 'array',
      items: {
        $ref: '#/definitions/bookChapter',
      },
    },
  },
  definitions: {
    ...commonDefinitions,
    bookChapter: bookChaptersSchema.definitions.bookChapter,
  },
};
